if (!customElements.get('product-card-slider')) {
  /**
   * <product-card-slider> — thin wrapper around Keen Slider for product cards.
   *
   * - Prev/next arrow buttons (hidden at slider ends) are the only way to
   *   change slides — drag/swipe is disabled.
   * - Click on a slide forwards to the product link (the slider sits above
   *   Dawn's `.card__heading a::after` overlay, so it has to re-implement
   *   the click-to-navigate behavior).
   */
  class ProductCardSlider extends HTMLElement {
    connectedCallback() {
      this.trackEl = this.querySelector('[data-card-slider-track]');
      this.prevBtn = this.querySelector('[data-card-slider-prev]');
      this.nextBtn = this.querySelector('[data-card-slider-next]');
      this.slideEls = Array.from(this.querySelectorAll('[data-card-slider-slide]'));

      if (!this.trackEl || this.slideEls.length < 2) return;
      if (typeof window.KeenSlider === 'undefined') {
        // Library hasn't finished loading yet; retry once on next frame.
        requestAnimationFrame(() => this.connectedCallback());
        return;
      }

      this.slider = new window.KeenSlider(this.trackEl, {
        loop: false,
        drag: false,
        renderMode: 'performance',
        slides: { perView: 1, spacing: 0 },
        created: () => this._updateNav(),
        slideChanged: () => this._updateNav(),
        optionsChanged: () => this._updateNav(),
      });

      this.prevBtn?.addEventListener('click', this._onPrev);
      this.nextBtn?.addEventListener('click', this._onNext);

      // Stop pointerdown on buttons from reaching Keen Slider's drag logic.
      [this.prevBtn, this.nextBtn].forEach((btn) => {
        btn?.addEventListener('pointerdown', (e) => e.stopPropagation());
      });

      this.slideEls.forEach((slide) => {
        slide.addEventListener('click', this._onSlideClick);
      });

      this.setAttribute('tabindex', '0');
      this.addEventListener('keydown', this._onKeyDown);
    }

    disconnectedCallback() {
      this.slider?.destroy();
      this.slider = null;
    }

    _updateNav = () => {
      if (!this.slider || !this.slider.track) return;
      const { rel, maxIdx } = this.slider.track.details;
      if (this.prevBtn) this.prevBtn.hidden = rel <= 0;
      if (this.nextBtn) this.nextBtn.hidden = rel >= maxIdx;
    };

    _onPrev = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.slider?.prev();
    };

    _onNext = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.slider?.next();
    };

    _onKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.slider?.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.slider?.next();
      }
    };

    _onSlideClick = (e) => {
      if (e.target.closest('[data-card-slider-prev], [data-card-slider-next]')) return;
      const cardLink = this.closest('.card-wrapper, .card')?.querySelector(
        '.card__heading a, a.full-unstyled-link'
      );
      if (cardLink?.href) {
        e.preventDefault();
        window.location.href = cardLink.href;
      }
    };
  }

  customElements.define('product-card-slider', ProductCardSlider);
}
